import React from 'react';

// material-ui
import { List, ListItemButton, ListItemText, Typography } from '@mui/material';

import { SearchResult } from '../dataObjects/KnowledgeTypes';

interface Props {
    results: SearchResult[];
    onOpen: (path: string, heading: string | null) => void;
}

/** "Title > Section > Subsection" without the title, which the result already shows. */
export const sectionOf = (result: SearchResult): string => {
    const parts = result.headingPath.split(' > ');
    return parts.length > 1 ? parts.slice(1).join(' › ') : '';
};

const pagesOf = (result: SearchResult): string => {
    if (result.pageStart == null) {
        return '';
    }
    return result.pageEnd != null && result.pageEnd !== result.pageStart
        ? `pp. ${result.pageStart}–${result.pageEnd}`
        : `p. ${result.pageStart}`;
};

const KnowledgeSearchResults = (props: Props): JSX.Element => {
    if (props.results.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No matching notes.</Typography>;
    }
    return (
        <List dense disablePadding>
            {props.results.map((result, i) => {
                const parts = result.headingPath.split(' > ');
                const heading = parts.length > 1 ? parts[parts.length - 1] : null;
                const section = sectionOf(result);
                const pages = pagesOf(result);
                return (
                    <ListItemButton key={`${result.path}-${i}`} alignItems="flex-start" onClick={() => props.onOpen(result.path, heading)}>
                        <ListItemText
                            primary={result.title}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 550 }}
                            secondary={
                                <>
                                    {(section || pages) &&
                                        <Typography component="span" variant="caption" display="block" color="primary">
                                            {[section, pages].filter(s => s).join(' · ')}
                                        </Typography>
                                    }
                                    <Typography component="span" variant="caption" display="block" color="text.secondary">
                                        {result.snippet}
                                    </Typography>
                                </>
                            }
                        />
                    </ListItemButton>
                );
            })}
        </List>
    );
};

export default KnowledgeSearchResults;
